import { FC, useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface SlugInputProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  size?: "half" | "full";
  placeholder?: string;
  disabled?: boolean;
  whatToWatch: string;
}

const SlugInput2: FC<SlugInputProps> = ({
  name,
  label,
  isRequired,
  size = "half",
  placeholder,
  disabled,
  whatToWatch,
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const [slug, setSlug] = useState("");
  const nameValue = watch(whatToWatch);

  // Generate a clean slug
  const generateSlug = (value: string) => {
    return value
      ?.toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // strip accents
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-") // replace non-latin chars with dash
      .replace(/^-+|-+$/g, "") // trim leading/trailing dashes
      .replace(/--+/g, "-"); // collapse multiple dashes
  };

  // Auto-generate slug from watched field (e.g. title)
  useEffect(() => {
    if (nameValue) {
      const newSlug = generateSlug(nameValue);
      setSlug(newSlug);
      setValue(name, newSlug); // set into form
    }
  }, [nameValue, setValue, name]);

  return (
    <div className="flex flex-col gap-1 mb-3 w-full">
      {label && (
        <label htmlFor={name} className="text-sm font-medium text-text mb-0">
          {t(label)}
          {isRequired && <span className="text-danger"> *</span>}
        </label>
      )}

      <div className={`${size === "half" ? "md:w-1/2 w-full" : "w-full"} relative mt-1`}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <input
              id={name}
              type="text"
              placeholder={placeholder ? t(placeholder) : ""}
              disabled={disabled}
              {...field}
              value={field.value || slug}
              onChange={(e) => {
                const newSlug = generateSlug(e.target.value);
                setSlug(newSlug);
                field.onChange(newSlug);
              }}
              className={`transition300 bg-inputBg block px-3 py-2 w-full border rounded-md shadow-sm 
                focus:outline-none focus:ring-primary focus:border-primary sm:text-sm ${
                  errors[name] ? "border-danger" : "border-inputBg"
                }`}
            />
          )}
        />
      </div>

      {errors[name] && (
        <p className="text-sm text-danger -mt-1">
          {String(errors[name]?.message)}
        </p>
      )}
    </div>
  );
};

export default SlugInput2;
